import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EmptyState } from '@/components/empty-state'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDateTime } from '@/lib/labels'

type PaymentRow = {
  id: string
  method: string
  provider: string
  status: string
  amountCents: number
  createdAt: string
  order: { reference: string; contactName: string; status: string }
}

function payTone(status: string) {
  if (status === 'CAPTURED' || status === 'AUTHORIZED') return 'ok'
  if (status === 'FAILED' || status === 'EXPIRED') return 'bad'
  return 'warn'
}

export function Paiements() {
  const toast = useToast()
  const qc = useQueryClient()
  const [captureId, setCaptureId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['admin-payments'],
    queryFn: () => api<{ items: PaymentRow[] }>('/admin/payments?limit=100'),
  })

  const capture = useMutation({
    mutationFn: () => apiMutate(`/admin/payments/${captureId}/capture-cod`, 'POST'),
    onSuccess: () => {
      toast.ok('Paiement COD encaissé')
      setCaptureId(null)
      void qc.invalidateQueries({ queryKey: ['admin-payments'] })
      void qc.invalidateQueries({ queryKey: ['admin-orders'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Aucun paiement" body="Les tentatives de paiement apparaîtront ici." />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Commande', 'Client', 'Méthode', 'Statut', 'Montant', 'Date', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const canCod =
                  p.method === 'CASH_ON_DELIVERY' &&
                  p.provider === 'MANUAL' &&
                  p.status !== 'CAPTURED'
                return (
                  <tr key={p.id} className="border-b border-bordure-admin last:border-0">
                    <td className="px-4 py-2.5">
                      <a
                        href={`/commandes/${p.order.reference}`}
                        className="font-medium text-encre-admin underline-offset-2 hover:underline"
                      >
                        {p.order.reference}
                      </a>
                    </td>
                    <td className="px-4 py-2.5">{p.order.contactName}</td>
                    <td className="px-4 py-2.5">{p.method}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill label={p.status} tone={payTone(p.status)} />
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{formatMoney(p.amountCents)}</td>
                    <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(p.createdAt)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <RowActions
                        items={[
                          {
                            label: 'Ouvrir commande',
                            onSelect: () => {
                              window.location.href = `/commandes/${p.order.reference}`
                            },
                          },
                          ...(canCod
                            ? [
                                {
                                  label: 'Marquer encaissé',
                                  onSelect: () => setCaptureId(p.id),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      <ConfirmDialog
        open={Boolean(captureId)}
        title="Marquer le COD comme encaissé ?"
        body="Le paiement sera capturé et la commande pourra passer en payée."
        confirmLabel="Encaisser"
        busy={capture.isPending}
        onClose={() => setCaptureId(null)}
        onConfirm={() => capture.mutate()}
      />
    </div>
  )
}
